import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { formAtom } from "@frontend/lib/form";
import { ApiError } from "@frontend/lib/http";

import { contractSchema } from "../schemas/contract";
import type { AmendmentValues, ContractValues } from "../schemas/contract";
import { contractApi } from "../services";
import type { ContractOptions } from "../types";
import { toContractValues } from "../utils/payload";

export type ContractEditorState = {
  projectId: number;
  contractId: number | null;
  options: ContractOptions;
  willTerminateCurrent: boolean;
  version: number | null;
  loading: boolean;
  saving: boolean;
  conflict: boolean;
  error: string | null;
};

const emptyOptions: ContractOptions = {
  contractGroups: [],
  contractDurations: [],
  endDelays: [],
  billPeriods: [],
  terminationReasons: [],
  priceTypes: [],
  paymentPeriods: [],
  paymentTypes: [],
  paymentTargets: [],
  subscriptionBillingTimes: [],
};

export const emptyAmendment = (): AmendmentValues => ({
  id: null,
  dateBegin: null,
  price: null,
  comments: null,
  deletable: true,
});

export const emptyContract = (): ContractValues => ({
  lockVersion: null,
  dateBegin: null,
  dateEnd: null,
  contractGroupId: null,
  contractDurationId: null,
  endDelayId: null,
  billPeriodId: null,
  postPeriodBilling: false,
  autoRenewal: true,
  renewalWarning: false,
  workedWeeksCount: null,
  terminationReasonId: null,
  price: null,
  priceType: null,
  amendments: [],
  commercialCondition: {
    subscriptionPaymentPeriodId: null,
    paymentTypeId: null,
    paymentEmail: null,
    paymentTargetId: null,
    subscriptionBillingTimeId: null,
    priceRevisionActivation: true,
  },
});

export const initialContractEditorState: ContractEditorState = {
  projectId: 0,
  contractId: null,
  options: emptyOptions,
  willTerminateCurrent: false,
  version: null,
  loading: true,
  saving: false,
  conflict: false,
  error: null,
};

export const contractForm = formAtom(contractSchema, emptyContract(), {
  normalizeNull: false,
});

export const contractEditorAtom = atomWithImmer<ContractEditorState>(
  initialContractEditorState,
);

export const optionsAtom = atom((get) => get(contractEditorAtom).options);

export type EditedAmendment = {
  index: number;
  created: boolean;
};

export const editedAmendmentAtom = atom<EditedAmendment | null>(null);

export const addAmendmentAtom = atom(null, (get, set) => {
  const index = get(contractForm.fields.amendments.valueAtom).length;

  set(contractForm.fields.amendments.insert, { value: emptyAmendment() });

  set(editedAmendmentAtom, { index, created: true });
});

export const cancelAmendmentEditAtom = atom(
  null,
  (get, set, snapshot: AmendmentValues | null) => {
    const edited = get(editedAmendmentAtom);

    if (null === edited) {
      return;
    }

    const itemAtom = get(contractForm.fields.amendments.itemAtomsAtom)[
      edited.index
    ];

    if (itemAtom) {
      if (edited.created) {
        set(contractForm.fields.amendments.remove, itemAtom);
      } else if (null !== snapshot) {
        set(itemAtom, snapshot);
      }
    }

    set(editedAmendmentAtom, null);
  },
);

export const loadContractEditorAtom = atom(
  null,
  async (_get, set, projectId: number, contractId: number | null) => {
    set(contractEditorAtom, (state) => {
      state.projectId = projectId;
      state.contractId = contractId;
      state.loading = true;
      state.error = null;
      state.conflict = false;
    });

    set(editedAmendmentAtom, null);

    try {
      const [options, loaded] = await Promise.all([
        contractApi.fetchOptions(projectId),
        null === contractId
          ? contractApi.fetchDraft(projectId)
          : contractApi
              .fetchContract(projectId, contractId)
              .then((contract) => ({
                contract,
                willTerminateCurrent: false,
              })),
      ]);

      set(contractForm.actions.setInitial, toContractValues(loaded.contract));

      set(contractEditorAtom, (state) => {
        state.options = options;
        state.willTerminateCurrent = loaded.willTerminateCurrent;
        state.version = loaded.contract.version;
        state.loading = false;
      });
    } catch (error: unknown) {
      set(contractEditorAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const saveContractAtom = atom(
  null,
  async (get, set): Promise<number | null> => {
    const { projectId, contractId } = get(contractEditorAtom);

    set(contractEditorAtom, (state) => {
      state.error = null;
      state.conflict = false;
      state.saving = true;
    });

    let savedId: number | null = null;

    try {
      const result = await set(contractForm.actions.submit, async (values) => {
        const saved =
          null === contractId
            ? await contractApi.createContract(projectId, values)
            : await contractApi.updateContract(projectId, contractId, values);

        savedId = saved.id;

        // The record as the API now holds it, lock version included.
        set(contractForm.actions.setInitial, toContractValues(saved));

        set(contractEditorAtom, (state) => {
          state.version = saved.version;
          state.willTerminateCurrent = false;
        });
      });

      if (!result.success) {
        return null;
      }
    } catch (error: unknown) {
      set(contractEditorAtom, (state) => {
        state.error = message(error);
        state.conflict = error instanceof ApiError && 409 === error.status;
      });

      if (error instanceof ApiError && 0 < error.violations.length) {
        set(contractForm.actions.setErrors, error.violations);
      }

      return null;
    } finally {
      set(contractEditorAtom, (state) => {
        state.saving = false;
      });
    }

    if (null !== savedId) {
      set(contractEditorAtom, (state) => {
        state.contractId = savedId;
      });
    }

    return savedId;
  },
);

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
